import { useState } from 'react'
import { Box, InputLabel, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material'
import { t } from 'i18next'
import { useMutation, useQueryClient } from 'react-query'

import { AlertType } from 'src/components/shared/ui/AlertDialog/enums'
import ButtonPrimaryOutlined from 'src/components/shared/ui/ButtonPrimaryOutlined'
import ButtonSecondary from 'src/components/shared/ui/ButtonSecondary'
import { AngleDownIcon2 } from 'src/components/shared/ui/Icons'
import RequiredText from 'src/components/views/EscritosLexnet/components/shared/components/RequiredText'
import { useAlertContext } from 'src/contexts/AlertContext'
import { Company, Expedient, Mensaje } from 'src/interfaces'
import { createRequest as createMensajeLexonClassification } from 'src/services/MensajeLexonClassification'
import ExpedientsTable from './ExpedientsTable'
import useStyles from './useStyles'

type Props = {
  message: Mensaje
  onCancel: () => void
  onAssigned: () => void
  companies: Company[]
}

function AssignExpedientForm({ message, onAssigned, onCancel, companies }: Props): JSX.Element {
  const classes = useStyles()
  const queryClient = useQueryClient()
  const [step, setStep] = useState(companies.length > 1 ? 0 : 1)
  const [error, setError] = useState<string | null>('')
  const [expedient, setExpedient] = useState<Expedient | null>(null)
  const [classificationName, setClassificationName] = useState<string | null>(
    message?.datos?.asunto
  )
  const [expireDate, setExpireDate] = useState<string | null>(message?.datos?.fechaVencimiento)
  const [company, setCompany] = useState<Company | null>(companies.length > 1 ? null : companies[0])
  const { addAlert } = useAlertContext()

  function showErrorDialog() {
    addAlert({
      message: t('dialog.message.clasificar_error'),
      type: AlertType.ERROR
    })
  }

  const createMensajeLexonClassificationMutation = useMutation(
    () => {
      if (expedient === null) {
        setError('Por favor, selecciona un expediente')
        return Promise.reject()
      }

      if (company === null) {
        setError('Por favor, selecciona una empresa')
        return Promise.reject()
      }

      return createMensajeLexonClassification(
        expedient.idExpedient,
        expedient.idOwnFile,
        expedient.nig,
        expedient.description,
        message.id,
        company.idCompany,
        company.name,
        classificationName,
        expireDate
      )
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['messageDetail', message.id])
        queryClient.invalidateQueries(['messagesListed'])
        onAssigned()
      },
      onError: () => showErrorDialog()
    }
  )

  function onChangeCompany(event: SelectChangeEvent) {
    const companyId = String(event.target.value)
    const selectedCompany = companies.find(c => c.idCompany.toString() === companyId)

    setCompany(selectedCompany || null)
  }

  function handleSubmitButton() {
    if (step === 0) {
      if (company) {
        setError(null)
        setStep(1)
      } else {
        setError('Por favor, selecciona una empresa')
      }
    } else if (step === 1) {
      createMensajeLexonClassificationMutation.mutate()
    }
  }

  function onChangeExpedient(value: any) {
    setExpedient(value)
  }

  function setClassification(value: any) {
    setClassificationName(value)
  }

  function goBack() {
    setStep(0)
  }

  return (
    <div className={classes.content}>
      <RequiredText />
      {step === 0 && (
        <Box className={classes.companyFormControl}>
          <InputLabel id="select-company-label" required>
            Empresa
          </InputLabel>
          <Select
            id="select-company"
            labelId="select-company-label"
            value={company === null ? '-1' : company.idCompany}
            onChange={onChangeCompany}
            variant="outlined"
            sx={{
              width: '100%',
              padding: 0
            }}
            IconComponent={_props => (
              <Box className="wrapperIcon">
                <AngleDownIcon2 className={_props.className.toString()} />
              </Box>
            )}
          >
            <MenuItem value="-1">Selecciona una opción</MenuItem>
            {companies.map(c => (
              <MenuItem key={c.idCompany} value={c.idCompany}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}
      {step === 1 && company !== null && (
        <ExpedientsTable
          onSelectExpedient={onChangeExpedient}
          setClassificationName={setClassification}
          setExpireDate={setExpireDate}
          company={company}
          classificationName={classificationName || undefined}
          expireDate={expireDate || undefined}
          selectedExpedient={expedient}
        />
      )}
      <div className={classes.formActionsWrapper}>
        {error && (
          <div className={classes.formError}>
            <Typography color="error">{error}</Typography>
          </div>
        )}
        <div className={classes.formActions}>
          <ButtonPrimaryOutlined onClick={onCancel}>Cancelar</ButtonPrimaryOutlined>

          {companies.length > 1 && step === 1 && (
            <ButtonPrimaryOutlined onClick={goBack}>Atrás</ButtonPrimaryOutlined>
          )}

          <ButtonSecondary
            disabled={
              step === 0
                ? !company
                : !expedient ||
                  !classificationName ||
                  createMensajeLexonClassificationMutation.isLoading
            }
            onClick={handleSubmitButton}
            loading={createMensajeLexonClassificationMutation.isLoading}
          >
            {step === 0 ? 'Continuar' : 'Guardar'}
          </ButtonSecondary>
        </div>
      </div>
    </div>
  )
}

export default AssignExpedientForm
